import { useNuxtApp } from '#imports';
import { useAuth0 } from '@auth0/auth0-vue';

/** ページロードを跨いで残る、無限リダイレクト防止用のガードキー。アプリ名で名前空間化し同一オリジンの他アプリと衝突しないようにする */
export const SESSION_RECOVERY_GUARD_KEY = 'datastore.session-recovery-attempted-at';

/** この時間窓の中での復旧試行回数を数える。1往復 (/authorize への遷移〜復帰) がこれを
 * 超えると上限に到達しなくなるため、低速回線・コールドキャッシュでも十分な余裕を持たせる */
export const RECOVERY_WINDOW_MS = 300_000;
/** この窓内で試行できる最大回数。超えたら諦める */
export const MAX_RECOVERY_ATTEMPTS = 2;

interface AttemptRecord {
  count: number;
  lastAttemptAt: number;
}

// @auth0/auth0-vue は redirect transaction (state/code_verifier/nonce) を
// `a0.spajs.txs.<clientId>` という clientId 単位の単一キーに保存する。並行して
// 2回 loginWithRedirect() を呼ぶと後発が transaction を上書きし、Auth0 からの
// コールバックが Invalid state で失敗するため、同一ページロード内では最初の
// 1件だけが復旧処理を行う。
let sessionRecoveryInFlightThisLoad = false;
// 同一ページロード内で一度諦めたら、それ以降の呼び出しは再試行せず毎回 fallback する
let sessionRecoveryGaveUpThisLoad = false;

/** `/` で始まらない、または `//`・`/\` を含む場合はオープンリダイレクトになりうるため既定値に丸める。
 * 認証系ルート (`/auth/...`) への復帰も、ログアウト遷移中に競合した場合などに無意味な
 * 往復を生むため既定値に丸める */
function safeRedirectTarget(value: string): string {
  if (
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.startsWith('/\\') ||
    value.startsWith('/auth/')
  ) {
    return '/';
  }
  return value;
}

/** 現在の URL (パス + クエリ + ハッシュ) を appState.target 用に返す */
export function currentRedirectTarget(): string {
  return safeRedirectTarget(
    window.location.pathname + window.location.search + window.location.hash,
  );
}

/**
 * テスト専用: モジュールスコープの状態をリセットする。テスト間の状態リークを防ぐためだけに使い、
 * 本番コードからは呼ばない。
 */
export function __resetSessionRecoveryForTests(): void {
  sessionRecoveryInFlightThisLoad = false;
  sessionRecoveryGaveUpThisLoad = false;
}

/**
 * loginWithRedirect() を開始してよいかを判定し、開始する場合は in-flight フラグを立てて true を返す。
 * MFA 必須分岐 (use-error-handler.ts) からの loginWithRedirect() 呼び出しとも共有し、同一ページ
 * ロード内での二重発火 (上記コメントの transaction 上書き) を防ぐ。
 */
export function beginRedirect(): boolean {
  if (sessionRecoveryInFlightThisLoad) return false;
  sessionRecoveryInFlightThisLoad = true;
  return true;
}

/** loginWithRedirect() の呼び出しが失敗し遷移が始まらなかったときに呼び、再試行を許可する */
export function endRedirect(): void {
  sessionRecoveryInFlightThisLoad = false;
}

// loginWithRedirect() は window.location.assign() でページ遷移するため通常はページごと離脱するが、
// ブラウザバックで bfcache から JS ヒープごと復元されると in-flight フラグが true のまま残り、
// 以降の 401 が beginRedirect() のガードで無音に吸収され続ける (無限スピナー化)。
// give-up 状態は意図的に維持する (bfcache 復元後も諦めた判断自体は覆さない)。
// このモジュールは SSR (window 不在) からも import されうるためガードする
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      endRedirect();
    }
  });
}

function isAttemptRecord(value: unknown): value is AttemptRecord {
  // count は非負整数に限定する。単なる number だと負の巨大値 (例: -1e308) を
  // count + 1 しても丸め誤差で値が変わらず、上限判定を恒久的に回避できてしまう
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as AttemptRecord).count === 'number' &&
    Number.isInteger((value as AttemptRecord).count) &&
    (value as AttemptRecord).count >= 0 &&
    typeof (value as AttemptRecord).lastAttemptAt === 'number'
  );
}

interface ReadAttemptRecordResult {
  record: AttemptRecord | null;
  /** false ならストレージ自体にアクセスできない環境。試行回数を追跡できないので
   * 「レコード無し (初回試行)」には丸めず、呼び出し側で復旧を諦めさせる */
  storageAvailable: boolean;
}

/** 壊れたレコード (不正 JSON・型不正) はストレージ自体は機能しているので「レコード無し (初回試行)」
 * として扱ってよいが、getItem 自体の例外は区別する (区別しないと fail-open になり、
 * 試行回数を追跡できず無限リダイレクトを止められなくなるため) */
function readAttemptRecord(): ReadAttemptRecordResult {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(SESSION_RECOVERY_GUARD_KEY);
  } catch {
    return { record: null, storageAvailable: false };
  }
  if (!raw) return { record: null, storageAvailable: true };
  try {
    const parsed: unknown = JSON.parse(raw);
    return { record: isAttemptRecord(parsed) ? parsed : null, storageAvailable: true };
  } catch {
    return { record: null, storageAvailable: true };
  }
}

/** 書き込みに失敗した場合、呼び出し側は試行回数を追跡できないとみなして復旧を諦める */
function writeAttemptRecord(record: AttemptRecord): boolean {
  try {
    sessionStorage.setItem(SESSION_RECOVERY_GUARD_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

/**
 * 遷移の開始自体に失敗したときの巻き戻し。再試行を許可するため gave-up 状態にはしない。
 * 書き込み済みの試行回数は消さない (loginWithRedirect() 呼び出し前の同期 throw ならそもそも
 * 書き込まれていないので影響なし。呼び出し後の非同期失敗なら、その試行はカウントに残る)
 */
function recoverFromRedirectFailure(fallback: () => void): void {
  endRedirect();
  fallback();
}

/** 窓内での試行回数が上限を超えた (ページロードを跨いだリダイレクトループの兆候) ため諦める */
function giveUp(fallback: () => void): void {
  sessionRecoveryGaveUpThisLoad = true;
  endRedirect();
  fallback();
}

/**
 * sophon の 401 (Single Logout での失効等) からの復旧エントリポイント。
 * loginWithRedirect() でトップレベルの /authorize へ遷移させ、Auth0 SSO セッションが
 * 生きている端末では黙って appState.target (元の画面) へ復帰させる。失効していれば
 * Auth0 のログイン画面が表示される。
 */
export function tryRecoverSession(fallback: () => void): void {
  if (sessionRecoveryGaveUpThisLoad) {
    // このロードでは既に諦めている。無音にせず毎回 fallback する
    fallback();
    return;
  }

  if (!beginRedirect()) {
    // 同一ページロード内で既に進行中 (MFA分岐など他の経路も含む)。後続分はここで黙って吸収する。
    return;
  }

  const now = Date.now();
  const { record: existing, storageAvailable } = readAttemptRecord();
  if (!storageAvailable) {
    // ストレージにアクセスできない環境で試行回数を追跡せずに進めると無限リダイレクトを
    // 止められなくなるため、機能を諦めて安全側 (fallback) に倒す
    endRedirect();
    fallback();
    return;
  }

  // 窓の起点を「最初の試行」ではなく「直近の試行」にしている。起点固定だと、1往復が窓より
  // 短くても累計時間が窓を超えた時点でリセットされ、上限に到達しないまま試行が続く。
  // なお 1往復自体が RECOVERY_WINDOW_MS を超える環境では、どちらの基準でも上限に到達しない。
  // 経過時間が負 (端末時計の巻き戻り) の場合も窓外として扱いレコードをリセットする。
  const elapsed = existing !== null ? now - existing.lastAttemptAt : null;
  const isWithinWindow =
    existing !== null && elapsed !== null && elapsed >= 0 && elapsed < RECOVERY_WINDOW_MS;
  const nextRecord: AttemptRecord = isWithinWindow
    ? { count: existing.count + 1, lastAttemptAt: now }
    : { count: 1, lastAttemptAt: now };

  if (nextRecord.count > MAX_RECOVERY_ATTEMPTS) {
    // レコードは書き込まない (更新すると窓が延び続けてしまうため)
    giveUp(fallback);
    return;
  }

  try {
    const nuxtApp = useNuxtApp();
    const auth0 = nuxtApp.runWithContext(() => useAuth0());
    // 実際に遷移を試みる直前に書き込む。useNuxtApp()/useAuth0() の同期 throw では
    // 書き込み前に catch へ抜けるため、その試行はカウントされない
    if (!writeAttemptRecord(nextRecord)) {
      // 書き込めない = 以降も試行回数を追跡できないため、無限リダイレクトを止められない
      endRedirect();
      fallback();
      return;
    }
    void auth0.loginWithRedirect({ appState: { target: currentRedirectTarget() } }).catch(() => {
      // 遷移の開始自体に失敗した場合のみここに来る (Auth0 側のログイン失敗は遷移後なので捕捉できない)
      recoverFromRedirectFailure(fallback);
    });
  } catch {
    // useNuxtApp()/useAuth0() がコンポーネントコンテキスト外で同期的に throw するケース
    recoverFromRedirectFailure(fallback);
  }
}
